import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Stack,
  Button,
  TextField,
  Typography,
  Card,
  CardContent,
  Grid,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody
} from '@mui/material';
import { useState } from 'react';
import { v4 as uuid } from 'uuid';
import { combinarFechaConHoraActual, normalizarFechaISO } from '../../../utils/horaLocal';
import { calcularEfectivoYQR } from '../services/cajaService';

const BILLETES = [
  { denominacion: 200, label: 'Billete 200' },
  { denominacion: 100, label: 'Billete 100' },
  { denominacion: 50, label: 'Billete 50' },
  { denominacion: 20, label: 'Billete 20' },
  { denominacion: 10, label: 'Billete 10' }
];

const MONEDAS = [
  { denominacion: 5, label: 'Moneda 5' },
  { denominacion: 2, label: 'Moneda 2' },
  { denominacion: 1, label: 'Moneda 1' },
  { denominacion: 0.5, label: 'Moneda 0.50' },
  { denominacion: 0.2, label: 'Moneda 0.20' },
  { denominacion: 0.1, label: 'Moneda 0.10' }
];

const corteInicial = () => ({
  ...BILLETES.reduce((acc, b) => ({ ...acc, [`b_${b.denominacion}`]: '' }), {}),
  ...MONEDAS.reduce((acc, m) => ({ ...acc, [`m_${m.denominacion}`]: '' }), {})
});

const calcularTotalContado = (cortes) => {
  let total = 0;
  BILLETES.forEach(b => { total += (parseFloat(cortes[`b_${b.denominacion}`]) || 0) * b.denominacion; });
  MONEDAS.forEach(m => { total += (parseFloat(cortes[`m_${m.denominacion}`]) || 0) * m.denominacion; });
  return total;
};

const formatearMonto = (monto) =>
  new Intl.NumberFormat('es-BO', { style: 'currency', currency: 'BOB' }).format(monto);

export function ModalArqueoCaja({
  open,
  onClose,
  state,
  fechaSeleccionada = null,
  díaBloqueado = false,
  onRegistrarArqueo = null,
  onRegistrarAjuste = null,
  onAnularArqueo = null
}) {
  const [cortes, setCortes] = useState(corteInicial);
  const [resultadoArqueo, setResultadoArqueo] = useState(null);
  const [ajusteActual, setAjusteActual] = useState(null);
  
  // Calcula el saldo teórico de caja (efectivo físico) acumulado hasta la fecha seleccionada,
  // usando la misma lógica que cajaService para consistencia con el Libro Diario.
  const calcularSaldoTeórico = () => {
    const hoy = normalizarFechaISO(fechaSeleccionada || new Date().toISOString()); // YYYY-MM-DD

    const movActivos = (state.movimientos || [])
      .filter(m => !m.anulado && normalizarFechaISO(m.fecha) <= hoy);

    const gastosActivos = (state.gastos || [])
      .filter(g => g.estado !== 'ANULADO' && normalizarFechaISO(g.fecha) <= hoy);

    const ajustesActivos = state.obtenerAjustes()
      .filter(a => normalizarFechaISO(a.fecha) <= hoy);

    const traspasosActivos = (state.traspasosQR || [])
      .filter(t => !t.anulado && normalizarFechaISO(t.fecha) <= hoy);

    const { totalEfectivo } = calcularEfectivoYQR({
      movimientos: movActivos,
      gastos: gastosActivos,
      ajustes: ajustesActivos,
      traspasos: traspasosActivos,
    });

    return totalEfectivo;
  };

  const handleSubmitArqueo = async () => {
    const saldoTeorico = calcularSaldoTeórico() || 0;
    const totalContado = calcularTotalContado(cortes);
    const diferencia = totalContado - saldoTeorico;
    const id = uuid();

    const nuevoArqueo = {
      id,
      fecha: combinarFechaConHoraActual(fechaSeleccionada),
      saldoTeorico,
      totalContado,
      diferencia,
      cortes: { ...cortes },
      estado: diferencia === 0 ? 'CORRECTO' : diferencia > 0 ? 'SOBRANTE' : 'FALTANTE'
    };

    // Guardar y obtener el objeto con el ID real del servidor
    let arqueoGuardado = { ...nuevoArqueo, id: id };
    if (onRegistrarArqueo) {
      const result = await onRegistrarArqueo(nuevoArqueo);
      if (result) arqueoGuardado = { ...arqueoGuardado, ...result };
    }

    setResultadoArqueo(arqueoGuardado);
    if (diferencia !== 0) setAjusteActual(arqueoGuardado);
  };

  const handleRegistrarAjuste = async () => {
    if (!ajusteActual) return;

    const arqueoDiaId = resultadoArqueo.id;
    const id = uuid();

    const nuevoAjuste = {
      id,
      fecha: combinarFechaConHoraActual(fechaSeleccionada),
      tipo: ajusteActual.diferencia > 0 ? 'SOBRANTE' : 'FALTANTE',
      monto: Math.abs(ajusteActual.diferencia),
      arqueoDiaId: arqueoDiaId, // ID real del servidor tras guardarArqueo
      descripcion: `${ajusteActual.diferencia > 0 ? 'Sobrante' : 'Faltante'} de arqueo de caja`,
      metodoPago: 'EFECTIVO',
    };

    const ajusteGuardado = { ...nuevoAjuste, id: id };
    console.log(ajusteGuardado)
    if (onRegistrarAjuste) {
      await onRegistrarAjuste(ajusteGuardado, resultadoArqueo);
    } else {
      await state.guardarAjuste(ajusteGuardado);
    }

    handleCerrar();
  };

  const handleAnularArqueoDelModal = async () => {
    const idArqueo = resultadoArqueo?.id;
    if (!idArqueo) return;

    if (onAnularArqueo) {
      await onAnularArqueo(idArqueo);
    } else {
      await state.anularAjustesPorArqueo(idArqueo);
    }

    handleCerrar();
  };

  const handleCerrar = () => {
    setCortes(corteInicial());
    setResultadoArqueo(null);
    setAjusteActual(null);
    onClose();
  };

  const totalContadoPreview = calcularTotalContado(cortes);

  if (!open) return null;

  return (
    <Dialog open={open} onClose={handleCerrar} fullWidth maxWidth="md">
      <DialogTitle sx={{ fontSize: '1.3rem', fontWeight: 'bold' }}>
        {resultadoArqueo ? 'Resultado del Arqueo' : 'Ingresa los Cortes de Caja'}
      </DialogTitle>

      <DialogContent sx={{ mt: 2 }}>
        {!resultadoArqueo ? (
          <Stack spacing={3}>
            <Box sx={{ bgcolor: '#fff3cd', p: 2, borderRadius: 2, border: '1px solid #ffc107' }}>
              <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#664d03' }}>
                📋 Instrucciones: Cuenta cada denominación desde tu caja y anota la cantidad.
              </Typography>
            </Box>

            <Box>
              <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2, color: '#1976d2' }}>
                💵 Billetes
              </Typography>
              <Grid container spacing={2}>
                {BILLETES.map(b => (
                  <Grid item xs={12} sm={6} key={`b_${b.denominacion}`}>
                    <TextField
                      label={b.label}
                      type="number"
                      fullWidth
                      value={cortes[`b_${b.denominacion}`]}
                      onChange={(e) => setCortes({ ...cortes, [`b_${b.denominacion}`]: e.target.value })}
                      inputProps={{ min: '0', step: '1' }}
                      placeholder="Cantidad de billetes"
                    />
                  </Grid>
                ))}
              </Grid>
            </Box>

            <Box>
              <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2, color: '#1976d2' }}>
                🪙 Monedas
              </Typography>
              <Grid container spacing={2}>
                {MONEDAS.map(m => (
                  <Grid item xs={12} sm={6} key={`m_${m.denominacion}`}>
                    <TextField
                      label={m.label}
                      type="number"
                      fullWidth
                      value={cortes[`m_${m.denominacion}`]}
                      onChange={(e) => setCortes({ ...cortes, [`m_${m.denominacion}`]: e.target.value })}
                      inputProps={{ min: '0', step: '1' }}
                      placeholder="Cantidad de monedas"
                    />
                  </Grid>
                ))}
              </Grid>
            </Box>

            <Card sx={{ bgcolor: '#e8f5e9', borderLeft: '4px solid #4caf50' }}>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Total Contado en Caja (Preview)
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#2e7d32' }}>
                  {formatearMonto(totalContadoPreview)}
                </Typography>
              </CardContent>
            </Card>
          </Stack>
        ) : (
          <Stack spacing={3}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Card sx={{ bgcolor: '#f3e5f5', height: '100%' }}>
                  <CardContent>
                    <Typography color="textSecondary" gutterBottom>Saldo Teórico (Sistema)</Typography>
                    <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                      {formatearMonto(resultadoArqueo.saldoTeorico)}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Card sx={{ bgcolor: '#e8f5e9', height: '100%' }}>
                  <CardContent>
                    <Typography color="textSecondary" gutterBottom>Total Contado (Físico)</Typography>
                    <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#2e7d32' }}>
                      {formatearMonto(resultadoArqueo.totalContado)}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            <Card
              sx={{
                bgcolor:
                  resultadoArqueo.estado === 'CORRECTO' ? '#c8e6c9' :
                  resultadoArqueo.estado === 'SOBRANTE' ? '#fff9c4' : '#ffccbc',
                borderLeft: `4px solid ${
                  resultadoArqueo.estado === 'CORRECTO' ? '#2e7d32' :
                  resultadoArqueo.estado === 'SOBRANTE' ? '#f57f17' : '#d84315'
                }`
              }}
            >
              <CardContent>
                <Typography
                  sx={{
                    fontWeight: 'bold',
                    fontSize: '1.5rem',
                    color:
                      resultadoArqueo.estado === 'CORRECTO' ? '#2e7d32' :
                      resultadoArqueo.estado === 'SOBRANTE' ? '#f57f17' : '#d84315'
                  }}
                >
                  {resultadoArqueo.estado === 'CORRECTO'
                    ? '✅ ARQUEO CORRECTO'
                    : resultadoArqueo.estado === 'SOBRANTE'
                    ? `⚠️ SOBRANTE: ${formatearMonto(resultadoArqueo.diferencia)}`
                    : `❌ FALTANTE: ${formatearMonto(Math.abs(resultadoArqueo.diferencia))}`}
                </Typography>
                <Typography variant="body2" sx={{ mt: 1 }}>
                  Diferencia: {formatearMonto(resultadoArqueo.diferencia)}
                </Typography>
              </CardContent>
            </Card>

            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                Resumen de Cortes
              </Typography>
              <Box sx={{ overflowX: 'auto' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                      <TableCell>Denominación</TableCell>
                      <TableCell align="right">Cantidad</TableCell>
                      <TableCell align="right">Subtotal</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {BILLETES.map(b => {
                      const cant = parseFloat(resultadoArqueo.cortes[`b_${b.denominacion}`]) || 0;
                      return cant > 0 ? (
                        <TableRow key={`b_${b.denominacion}`}>
                          <TableCell>{b.label}</TableCell>
                          <TableCell align="right">{cant}</TableCell>
                          <TableCell align="right">{formatearMonto(cant * b.denominacion)}</TableCell>
                        </TableRow>
                      ) : null;
                    })}
                    {MONEDAS.map(m => {
                      const cant = parseFloat(resultadoArqueo.cortes[`m_${m.denominacion}`]) || 0;
                      return cant > 0 ? (
                        <TableRow key={`m_${m.denominacion}`}>
                          <TableCell>{m.label}</TableCell>
                          <TableCell align="right">{cant}</TableCell>
                          <TableCell align="right">{formatearMonto(cant * m.denominacion)}</TableCell>
                        </TableRow>
                      ) : null;
                    })}
                  </TableBody>
                </Table>
              </Box>
            </Box>
          </Stack>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2, gap: 1, flexDirection: 'column' }}>
        {resultadoArqueo ? (
          ajusteActual ? (
            <Stack direction="row" spacing={1} sx={{ width: '100%' }}>
              <Button fullWidth variant="outlined" color="error" onClick={handleAnularArqueoDelModal}>
                Anular Arqueo
              </Button>
              <Button fullWidth variant="contained" color="success" onClick={handleRegistrarAjuste}>
                Registrar Ajuste
              </Button>
            </Stack>
          ) : (
            <Button fullWidth variant="contained" onClick={handleCerrar}>
              Cerrar
            </Button>
          )
        ) : (
          <Stack direction="row" spacing={1} sx={{ width: '100%' }}>
            <Button fullWidth variant="outlined" onClick={handleCerrar}>Cancelar</Button>
            <Button fullWidth variant="contained" onClick={handleSubmitArqueo}>
              Enviar Cortes
            </Button>
          </Stack>
        )}
      </DialogActions>
    </Dialog>
  );
}
